import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Box,
  Typography,
  Paper,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { Search, VolumeUp, Close } from "@mui/icons-material";
import axios from "axios";
import { apiURL } from "../constant";

const QuickSearch = () => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setLoading(true);
    setError("");
    try {
      // Lấy thông tin từ dictionary API
      const dictResponse = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${searchTerm.trim()}`
      );
      const dictData = await dictResponse.json();

      if (dictData.length > 0) {
        // Lấy phát âm từ API của bạn
        const audioResponse = await axios.get(
          `${apiURL}/api/audio?word=${searchTerm.trim()}`
        );

        setResult({
          word: dictData[0].word,
          phonetic: dictData[0].phonetic,
          audio:
            audioResponse.data?.audioUrl || dictData[0].phonetics[0]?.audio,
          meanings: dictData[0].meanings.map((meaning) => ({
            partOfSpeech: meaning.partOfSpeech,
            definitions: meaning.definitions.map((def) => def.definition),
          })),
        });
      } else {
        setError("Không tìm thấy từ này");
        setResult(null);
      }
    } catch (error) {
      setError("Có lỗi xảy ra khi tìm kiếm");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAudio = () => {
    if (result?.audio) {
      const audio = new Audio(result.audio);
      audio.play();
    }
  };

  return (
    <>
      <IconButton
        color="primary"
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          backgroundColor: "white",
          boxShadow: 2,
          "&:hover": {
            backgroundColor: "grey.100",
          },
        }}
      >
        <Search />
      </IconButton>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Tra cứu nhanh
            </Typography>
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Nhập từ cần tra"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={handleSearch}>
                    <Search />
                  </IconButton>
                ),
              }}
            />
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Typography color="error" sx={{ my: 2 }}>
              {error}
            </Typography>
          )}

          {result && (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" sx={{ flexGrow: 1 }}>
                  {result.word}
                </Typography>
                {result.audio && (
                  <IconButton onClick={handlePlayAudio}>
                    <VolumeUp />
                  </IconButton>
                )}
              </Box>
              {result.phonetic && (
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {result.phonetic}
                </Typography>
              )}
              {result.meanings.map((meaning, index) => (
                <Box key={index} sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontStyle: "italic", color: "primary.main" }}
                  >
                    {meaning.partOfSpeech}
                  </Typography>
                  <List>
                    {meaning.definitions.map((def, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={def} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </Paper>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickSearch;
